/**
 * 複数の音声を「放送キュー順」に連続再生するユーティリティ — Issue #439。
 *
 * ## 何のためにあるか
 *
 * 放送地点は「開始チャイム → contents[] を並び順に → 終了チャイム」という
 * キューで放送される。個別プレビューだけでは
 *
 *   - チャイムと本文の間が詰まりすぎ / 空きすぎ
 *   - コンテンツの並び順が意図と違う
 *
 * といった **通しで聴かないと絶対に気付けない** 問題を発見できない。
 *
 * ## サーバ側で ffmpeg 結合する必要はない
 *
 * `<audio>` の `ended` イベントを繋いでプレイリスト再生すれば十分なので、
 * 音声結合用の Lambda / ffmpeg レイヤーを増やさずに実現できる。
 */

/** 連続再生する 1 要素 */
export interface SequentialAudioItem {
  /** 再生する音声の URL (blob: / presigned URL のいずれでも良い) */
  url: string;
  /**
   * この要素の再生速度 (`playback_rate`)。
   * 範囲 (0.5〜2.0) 外 / 未指定は 1.0 (等倍) に落とす。
   */
  playbackRate?: number;
  /** ログ / エラーメッセージ用の表示名 (例: 「開始チャイム」「#1 自列車遅延」) */
  label?: string;
}

/** `playbackRate` を許容範囲に丸める (`useAudioPreview` と同じ規則) */
function normalizePlaybackRate(rate: number | undefined): number {
  return typeof rate === 'number' && rate >= 0.5 && rate <= 2.0 ? rate : 1.0;
}

/**
 * 音声を 1 つ再生し、再生完了 (`ended`) を待つ。
 *
 * `HTMLAudioElement` を直接生成するのは、React のレンダリングツリーに
 * 一時的な `<audio>` を差し込む必要がなく、キュー再生の実装が単純になるため。
 *
 * @param audioFactory テストから `HTMLAudioElement` を差し替えるためのファクトリ
 */
export function playOne(
  item: SequentialAudioItem,
  audioFactory: (url: string) => HTMLAudioElement = (url) => new Audio(url),
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const audio = audioFactory(item.url);
    const rate = normalizePlaybackRate(item.playbackRate);
    // `src` 設定直後は playbackRate がリセットされることがあるため、
    // 生成直後と loadedmetadata の 2 か所で適用する
    // (`useAudioPreview` の useEffect + onLoadedMetadata と同じ二重適用パターン)。
    audio.playbackRate = rate;
    audio.onloadedmetadata = () => {
      audio.playbackRate = rate;
    };
    audio.onended = () => resolve();
    audio.onerror = () => reject(new Error(`${item.label ?? '音声'}の再生に失敗しました`));
    const started = audio.play();
    // `play()` は Promise を返す実装 (ブラウザ) と返さない実装 (jsdom 等) がある。
    // 自動再生ブロック等の即時エラーはここで拾う。
    if (started && typeof started.catch === 'function') {
      started.catch((err: unknown) =>
        reject(
          new Error(
            `${item.label ?? '音声'}の再生を開始できませんでした: ${(err as Error).message}`,
          ),
        ),
      );
    }
  });
}

/**
 * 音声を「配列の順番どおりに」1 つずつ再生する。
 *
 * 途中でエラーが起きた場合はそこで中断して例外を投げる
 * (以降の要素は再生しない。どこで失敗したかを `label` 付きで伝える)。
 *
 * @param items 再生する音声の配列 (放送キュー順)
 * @param audioFactory テストから `HTMLAudioElement` を差し替えるためのファクトリ
 */
export async function playSequential(
  items: SequentialAudioItem[],
  audioFactory?: (url: string) => HTMLAudioElement,
): Promise<void> {
  for (const item of items) {
    await playOne(item, audioFactory);
  }
}
